import { BaseObserver } from '@powersync/common';
import { asyncNotifier } from '../../../utils/async.js';
const pending = { state: 'pending' };
export class CheckpointStateSignals {
    currentState = pending;
    stateChanged = new BaseObserver();
    /**
     * Use to immediately restart another download iteration if it is retry delay when a new checkpoint is requested.
     */
    waitingForCheckpointsReady = asyncNotifier();
    updateState(state) {
        this.currentState = state;
        this.stateChanged.iterateListeners((l) => l.stateChanged?.());
    }
    /**
     * Marks the current download iteration as ended, blocking new checkpoint requests until the seed performed in the
     * next iteration.
     */
    downloadIterationEnded() {
        this.updateState(pending);
        // Checkpoint waiters called after this should be able to resume the download iteration.
        this.waitingForCheckpointsReady = asyncNotifier();
    }
    /**
     * Marks the sync client as disconnected, failing all outstanding checkpoint requests and preventing new ones.
     */
    disconnected() {
        this.updateState({ state: 'disconnected' });
    }
    /**
     * Returns a promise that resolves with the abort signal or when we have a waiter wanting to request a checkpoint.
     */
    waitForCheckpointWaiter(signal) {
        return this.waitingForCheckpointsReady.waitForNotification(signal);
    }
    markCheckpointsReady(completion) {
        return completion.then((_) => this.updateState({ state: 'ready' }), (error) => {
            this.updateState({ state: 'error', error });
            throw error;
        });
    }
    /**
     * Waits until a download iteration is active and has seeded the checkpoint state, meaning that checkpoint ids can
     * safely be allocated.
     *
     * @returns Turue if checkpoints are ready, false if aborted.
     */
    async waitForCheckpointRequestsReady(abort, wakeDownloadLoop = true) {
        return new Promise((resolve, reject) => {
            // A retry can reach this waiter after disconnect has already aborted its signal.
            if (abort.aborted) {
                resolve(false);
                return;
            }
            // Resolves the promise from the current state if possible, returning true if it was.
            const handleState = () => {
                const state = this.currentState;
                switch (state.state) {
                    case 'disconnected':
                        reject(cannotRequestDueToDisconnectedError());
                        return true;
                    case 'ready':
                        resolve(true);
                        return true;
                    case 'error':
                        reject(state.error);
                        return true;
                    case 'pending': {
                        // Wait for the next event.
                        if (wakeDownloadLoop) {
                            this.waitingForCheckpointsReady.notify();
                        }
                        return false;
                    }
                }
            };
            if (handleState()) {
                return;
            }
            let removeListener;
            let onAbort;
            function cleanup() {
                removeListener();
                abort.removeEventListener('abort', onAbort);
            }
            onAbort = () => {
                resolve(false);
                cleanup();
            };
            removeListener = this.stateChanged.registerListener({
                stateChanged() {
                    if (handleState()) {
                        cleanup();
                    }
                }
            });
            abort.addEventListener('abort', onAbort);
        });
    }
}
export function cannotRequestDueToDisconnectedError() {
    return new Error('Cannot request checkpoints, sync client is disconnected.');
}
export function checkpointRequestsNotEnabledError() {
    return new Error('Connected with legacy checkpoint mode, cannot request checkpoints.');
}
export function isCheckpointRequestApplied(status, requestId) {
    const applied = status?.internal_last_applied_checkpoint_request_id;
    return applied != null && BigInt(applied) >= requestId;
}
//# sourceMappingURL=CheckpointState.js.map